/**
 * "My taste" similarity scoring (client-side, free).
 *
 * Builds a per-user taste profile from the events the user has saved via the
 * 👍 Save reaction (writes kind=`taste` in `lib/favorites.tsx`), then scores any
 * candidate event for similarity. Used by:
 *   - HomePage / EventsMapPage "Recommended" toggle (filter + sort).
 *   - MyTastePage Recommended tab (sort by score + "why?" tooltip).
 *
 * Design choices:
 *  - Weights normalized 0..1 to keep the final score bounded.
 *  - Term-frequency weighting on tags/category/eventType so a tag that
 *    appears in 4 out of 5 marked events counts more than one appearing
 *    once.
 *  - Pure functions, no React/state. Safe to call inside a useMemo.
 *  - `scoreEvent` returns both the numeric score and a sorted reasons[]
 *    array so the UI can render "Matches: live-music (you marked 4),
 *    shared venue: Lemon Bar".
 *
 * Future-proofing: a Phase-7 embedding re-rank will compose with this by
 * taking the top-N tag-scored candidates and re-ranking via cosine
 * similarity vs. the centroid of marked-event embeddings. The reasons
 * model already supports an additional `'embedding'` kind for that.
 */
export interface ScorableEvent {
  rowKey: string;
  tags?: string[];
  category?: string;
  eventType?: string;
  venueId?: string;
  venueName?: string;
  seriesId?: string;
  seriesName?: string;
  neighborhood?: string;
}

/** Minimum number of marked events before scoring is meaningful. */
export const MIN_TASTE_FOR_SCORING = 3;

/** Below this score, "My taste only" filter hides the event. Tunable. */
export const MIN_TASTE_SCORE_THRESHOLD = 0.15;

/** Maximum reasons returned per scored event (top by contribution). */
const MAX_REASONS = 3;

/** Relative weight of each signal in the final score. Sum doesn't have to
 *  equal 1.0 — the final score is clamped to [0, 1]. These are the
 *  baseline weights used when the user hasn't customized anything; they
 *  are also the values of the `balanced` preset below. */
const W: Weights = {
  tags: 0.50,
  category: 0.15,
  eventType: 0.15,
  venue: 0.20,
  series: 0.30,
  neighborhood: 0.05,
  /** Semantic embedding similarity. Composes with tag-based signals as a
   *  separate axis — see `scoreEvent` for the blend formula. */
  embedding: 0.45
};

/**
 * The 7 signal axes that drive scoring. The frontend is the source of
 * truth for which keys exist; the backend just stores+returns whatever
 * JSON shape we send (validated to clamp values to [0, 1]).
 */
export type WeightKey =
  | 'tags'
  | 'category'
  | 'eventType'
  | 'venue'
  | 'series'
  | 'neighborhood'
  | 'embedding';

export type Weights = Record<WeightKey, number>;

export const WEIGHT_KEYS: ReadonlyArray<WeightKey> = [
  'tags', 'category', 'eventType', 'venue', 'series', 'neighborhood', 'embedding'
];

/** Short human-friendly labels for each signal (used in the settings UI). */
export const WEIGHT_LABELS: Record<WeightKey, string> = {
  tags:         'Tags',
  category:     'Category',
  eventType:    'Event type',
  venue:        'Familiar venues',
  series:       'Familiar series',
  neighborhood: 'Neighborhood',
  embedding:    'Vibe (semantic)'
};

/** One-line explanation of each signal for tooltips. */
export const WEIGHT_DESCRIPTIONS: Record<WeightKey, string> = {
  tags:         'Overlap with tags on events you\u2019ve marked.',
  category:     'Same broad category (e.g. music, food, market).',
  eventType:    'Same event type (e.g. concert, brunch, trivia).',
  venue:        'Hosted at a venue you\u2019ve marked before.',
  series:       'Part of a recurring series you\u2019ve marked.',
  neighborhood: 'In a neighborhood you\u2019ve marked.',
  embedding:    'Semantic similarity to the vibe of your marked events.'
};

/** Built-in presets the user can pick from. `balanced` doubles as the
 *  factory default — it is exactly the historical hardcoded `W`. */
export const TASTE_PRESETS = {
  balanced:    { tags: 0.50, category: 0.15, eventType: 0.15, venue: 0.20, series: 0.30, neighborhood: 0.05, embedding: 0.45 },
  familiar:    { tags: 0.55, category: 0.20, eventType: 0.20, venue: 0.40, series: 0.50, neighborhood: 0.10, embedding: 0.20 },
  adventurous: { tags: 0.45, category: 0.10, eventType: 0.10, venue: 0.05, series: 0.10, neighborhood: 0.00, embedding: 0.70 },
  local:       { tags: 0.45, category: 0.15, eventType: 0.15, venue: 0.30, series: 0.30, neighborhood: 0.45, embedding: 0.30 }
} as const satisfies Record<string, Weights>;

export type PresetName = keyof typeof TASTE_PRESETS;

export const DEFAULT_WEIGHTS: Weights = { ...TASTE_PRESETS.balanced };

/** Floating-point fuzz for preset matching — sliders snap to 0.01. */
const PRESET_EPSILON = 0.005;

/** Return the preset name whose values match `weights` within `PRESET_EPSILON`,
 *  or `null` when the weights are off-preset ("Custom"). */
export function findMatchingPreset(weights: Weights): PresetName | null {
  for (const [name, values] of Object.entries(TASTE_PRESETS) as Array<[PresetName, Weights]>) {
    let allClose = true;
    for (const key of WEIGHT_KEYS) {
      if (Math.abs((values[key] ?? 0) - (weights[key] ?? 0)) > PRESET_EPSILON) {
        allClose = false;
        break;
      }
    }
    if (allClose) return name;
  }
  return null;
}

/** True when both weights objects agree within `PRESET_EPSILON` on every key. */
export function weightsEqual(a: Weights, b: Weights): boolean {
  for (const key of WEIGHT_KEYS) {
    if (Math.abs((a[key] ?? 0) - (b[key] ?? 0)) > PRESET_EPSILON) return false;
  }
  return true;
}

/** Coerce an arbitrary partial object into a full `Weights`, clamping every
 *  value to [0, 1] and falling back to `DEFAULT_WEIGHTS` for missing keys.
 *  Used when hydrating from localStorage or the API where the shape may
 *  have drifted (e.g. a signal added in a later release). */
export function normalizeWeights(input: unknown): Weights {
  const out: Weights = { ...DEFAULT_WEIGHTS };
  if (!input || typeof input !== 'object') return out;
  const raw = input as Record<string, unknown>;
  for (const key of WEIGHT_KEYS) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      out[key] = Math.max(0, Math.min(1, v));
    }
  }
  return out;
}

/** Minimum cosine similarity to emit an `embedding` reason (avoid noise). */
const EMBEDDING_REASON_THRESHOLD = 0.55;

export type TasteReasonKind =
  | 'tag'
  | 'category'
  | 'eventType'
  | 'venue'
  | 'series'
  | 'neighborhood'
  | 'embedding';

export interface TasteReason {
  kind: TasteReasonKind;
  /** Human-readable label, e.g. "live-music", "Lemon Bar", "Murray Hill". */
  label: string;
  /** Contribution to the score in [0, 1]. */
  contribution: number;
}

export interface TasteProfile {
  /** Tag → term frequency in marked set (0..1). */
  tagWeights: Map<string, number>;
  /** Category → term frequency. */
  categoryWeights: Map<string, number>;
  /** EventType → term frequency. */
  eventTypeWeights: Map<string, number>;
  /** Venues seen in marked set. */
  venueIds: Set<string>;
  /** Series seen in marked set. */
  seriesIds: Set<string>;
  /** Neighborhood → term frequency (low-weight signal). */
  neighborhoodWeights: Map<string, number>;
  /** Count of marked events used to build the profile. */
  size: number;
  /** Lookup for "is this event itself in the training set" (used to dedupe). */
  markedRowKeys: Set<string>;
}

const EMPTY_PROFILE: TasteProfile = {
  tagWeights: new Map(),
  categoryWeights: new Map(),
  eventTypeWeights: new Map(),
  venueIds: new Set(),
  seriesIds: new Set(),
  neighborhoodWeights: new Map(),
  size: 0,
  markedRowKeys: new Set()
};

function bumpTermFrequency(map: Map<string, number>, key: string | undefined, total: number): void {
  if (!key) return;
  const k = key.toLowerCase();
  // Stored as raw count first; we normalize to /total at the end.
  map.set(k, (map.get(k) ?? 0) + 1 / total);
}

/**
 * Build a TasteProfile from the events the user has marked as "taste".
 * Returns the empty profile if fewer than {@link MIN_TASTE_FOR_SCORING}
 * events are provided — callers should check `profile.size` before scoring.
 */
/** Minimal event shape needed to build a taste profile. Lets callers pass
 *  lightweight persisted snapshots (see `lib/tasteMatch.tsx`) without carrying
 *  the full {@link ExtractedEvent} payload. */
export type TasteProfileInput = Pick<
  ScorableEvent,
  'rowKey' | 'tags' | 'category' | 'eventType' | 'venueId' | 'seriesId' | 'neighborhood'
>;

export function buildTasteProfile(markedEvents: ReadonlyArray<TasteProfileInput>): TasteProfile {
  if (markedEvents.length === 0) return EMPTY_PROFILE;
  const total = markedEvents.length;
  const profile: TasteProfile = {
    tagWeights: new Map(),
    categoryWeights: new Map(),
    eventTypeWeights: new Map(),
    venueIds: new Set(),
    seriesIds: new Set(),
    neighborhoodWeights: new Map(),
    size: total,
    markedRowKeys: new Set(markedEvents.map(e => e.rowKey))
  };
  for (const e of markedEvents) {
    for (const t of e.tags ?? []) bumpTermFrequency(profile.tagWeights, t, total);
    bumpTermFrequency(profile.categoryWeights, e.category, total);
    bumpTermFrequency(profile.eventTypeWeights, e.eventType, total);
    if (e.venueId) profile.venueIds.add(e.venueId);
    if (e.seriesId) profile.seriesIds.add(e.seriesId);
    bumpTermFrequency(profile.neighborhoodWeights, e.neighborhood, total);
  }
  return profile;
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Score a candidate event against the profile.
 *
 * Returns:
 *   - `score`: 0..1, where 1.0 = identical taste-signal match.
 *   - `reasons`: top-{@link MAX_REASONS} contributing signals, sorted desc.
 *
 * `score === 0` when there is no overlap or the profile is empty.
 */
export function scoreEvent(
  event: ScorableEvent,
  profile: TasteProfile,
  /**
   * Optional cosine similarity (typically in [-1, 1], in practice [0, 1] for
   * `text-embedding-3-small`) of this event vs. the centroid of the user's
   * marked-event embeddings. When provided and >= 0, contributes an
   * additional `embedding` axis weighted by `weights.embedding`. Pass
   * `undefined` for events the server didn't score (e.g. missing
   * embedding) — the tag-based score stands alone.
   */
  embeddingScore?: number,
  /**
   * Optional per-user weight overrides. Defaults to {@link DEFAULT_WEIGHTS}
   * (the historical `balanced` preset) so existing callers behave
   * identically. The settings UI in MyTastePage passes the user's saved
   * weights here so adjustments take effect everywhere scoring runs.
   */
  weights: Weights = W
): { score: number; reasons: TasteReason[] } {
  if (profile.size === 0) return { score: 0, reasons: [] };

  const reasons: TasteReason[] = [];

  // Tag overlap — weighted by term frequency in the marked set.
  let tagContribution = 0;
  if (event.tags && event.tags.length > 0) {
    const eventTagSet = new Set(event.tags.map(t => t.toLowerCase()));
    // Sum of weights of intersecting tags, normalized by sum of all tag weights
    // (so a fully-matched tag set scores 1.0 of the tag signal).
    let intersectingWeight = 0;
    let totalWeight = 0;
    for (const [tag, weight] of profile.tagWeights) {
      totalWeight += weight;
      if (eventTagSet.has(tag)) intersectingWeight += weight;
    }
    if (totalWeight > 0) {
      tagContribution = (intersectingWeight / totalWeight) * weights.tags;
      if (tagContribution > 0) {
        // Show the strongest single tag match in the reasons list.
        let bestTag: string | null = null;
        let bestTagWeight = 0;
        for (const tag of eventTagSet) {
          const w = profile.tagWeights.get(tag) ?? 0;
          if (w > bestTagWeight) { bestTagWeight = w; bestTag = tag; }
        }
        if (bestTag) {
          reasons.push({
            kind: 'tag',
            label: bestTag,
            contribution: tagContribution
          });
        }
      }
    }
  }

  // Category match (single-valued, so all-or-nothing weighted by TF).
  let categoryContribution = 0;
  if (event.category) {
    const w = profile.categoryWeights.get(event.category.toLowerCase()) ?? 0;
    categoryContribution = w * weights.category;
    if (categoryContribution > 0) {
      reasons.push({ kind: 'category', label: event.category, contribution: categoryContribution });
    }
  }

  // EventType match.
  let eventTypeContribution = 0;
  if (event.eventType) {
    const w = profile.eventTypeWeights.get(event.eventType.toLowerCase()) ?? 0;
    eventTypeContribution = w * weights.eventType;
    if (eventTypeContribution > 0) {
      reasons.push({ kind: 'eventType', label: event.eventType, contribution: eventTypeContribution });
    }
  }

  // Shared venue boost.
  let venueContribution = 0;
  if (event.venueId && profile.venueIds.has(event.venueId)) {
    venueContribution = weights.venue;
    reasons.push({
      kind: 'venue',
      label: event.venueName ?? event.venueId,
      contribution: venueContribution
    });
  }

  // Shared series boost.
  let seriesContribution = 0;
  if (event.seriesId && profile.seriesIds.has(event.seriesId)) {
    seriesContribution = weights.series;
    reasons.push({
      kind: 'series',
      label: event.seriesName ?? event.seriesId,
      contribution: seriesContribution
    });
  }

  // Neighborhood (small signal).
  let neighborhoodContribution = 0;
  if (event.neighborhood) {
    const w = profile.neighborhoodWeights.get(event.neighborhood.toLowerCase()) ?? 0;
    neighborhoodContribution = w * weights.neighborhood;
    if (neighborhoodContribution > 0) {
      reasons.push({
        kind: 'neighborhood',
        label: event.neighborhood,
        contribution: neighborhoodContribution
      });
    }
  }

  const score = clamp01(
    tagContribution +
    categoryContribution +
    eventTypeContribution +
    venueContribution +
    seriesContribution +
    neighborhoodContribution +
    // Embedding axis: clamp cosine into [0, 1] then weight. Skipped when the
    // server didn't return a score for this event.
    (typeof embeddingScore === 'number' && embeddingScore > 0
      ? clamp01(embeddingScore) * weights.embedding
      : 0)
  );

  if (typeof embeddingScore === 'number' && embeddingScore >= EMBEDDING_REASON_THRESHOLD) {
    reasons.push({
      kind: 'embedding',
      label: 'Similar to your taste',
      contribution: clamp01(embeddingScore) * weights.embedding
    });
  }

  // Sort reasons by contribution desc, keep top N.
  reasons.sort((a, b) => b.contribution - a.contribution);
  return { score, reasons: reasons.slice(0, MAX_REASONS) };
}

/** Convenience: produce a short user-facing string for a reason. */
export function formatTasteReason(r: TasteReason): string {
  switch (r.kind) {
    case 'tag':          return `Tag: ${r.label}`;
    case 'category':     return `Category: ${r.label}`;
    case 'eventType':    return `Type: ${r.label}`;
    case 'venue':        return `Venue you've marked: ${r.label}`;
    case 'series':       return `Series you've marked: ${r.label}`;
    case 'neighborhood': return `Neighborhood: ${r.label}`;
    case 'embedding':    return `Similar to your taste`;
  }
}
